import React, { useRef, useEffect, useMemo } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Html, useTexture, useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import allData from './projectsData.json'
import { Phone3DModel } from './Phone3DModel.jsx'
import { AnnotationPoint } from './AnnotationPoint.jsx'
import screenAnnotationsData from './screenAnnotationsData.json'

const { experiences: experiencesData, projects: projectsData } = allData

// use srgb colorspace for textures
THREE.Texture.DEFAULT_COLOR_SPACE = THREE.SRGBColorSpace
THREE.WebGLRenderer.prototype.outputEncoding = THREE.sRGBEncoding

/**
 * Spacing Constants
 * Used consistently for both position calculation and rendering
 */
const STEP_SPACING = 4 // Distance between steps within a subsection
const SECTION_SPACING = 24 // Distance between subsections

/**
 * workOverlay Component
 * 
 * Main 3D scene for the canvas work gallery
 * the different levels of content:
 * - Level 1: Experiences list
 * - Level 2: Client projects
 * - Level 3: Project details with UX flows
 */

const workOverlay = ({
  currentLevel,
  selectedClient,
  selectedProject,
  currentItemIndex,
  onSelectClient,
  onSelectProject,
  focusedIndex,
  setFocusedIndex,
  annotationsEnabled = true
}) => {
  const { camera } = useThree()
  const sceneGroupRef = useRef()
  const targetScenePosition = useRef(new THREE.Vector3(0, 0, 0))
  const currentScenePosition = useRef(new THREE.Vector3(0, 0, 0))
  const previousLevelRef = useRef(currentLevel)
  const animationProgress = useRef(1) // 0 to 1, tracks animation completion
  
  // Hover state for phone screens to show annotations
  const [hoveredPhoneIndex, setHoveredPhoneIndex] = React.useState(null)
  
  // Level transition animation state
  const isLevelTransitioning = useRef(false)
  const levelTransitionOffset = useRef(0) // Extra offset for level transition animation
  const targetLevelOffset = useRef(0)
  
  // Display state - what's currently rendered (may lag behind props during transition)
  const [displayLevel, setDisplayLevel] = React.useState(currentLevel)
  const [displayClient, setDisplayClient] = React.useState(selectedClient)
  const [displayProject, setDisplayProject] = React.useState(selectedProject)
  const isInitialMount = useRef(true)
  
  // Detect mobile viewport
  const [isMobile, setIsMobile] = React.useState(false)
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Set camera to look at the scene (forward facing)
  useEffect(() => {
    camera.lookAt(0, 0, 0)
  }, [camera])

  // Pre-calculate all step positions for ALL levels
  // Use DISPLAY level/client/project so positions don't change mid-animation
  const allStepPositions = useMemo(() => {
    let sections = []
    
    if (displayLevel === 1) {
      sections = experiencesData.sections || []
    } else if (displayLevel === 2 && displayClient) {
      const clientProjects = projectsData[displayClient] || []
      // Transform projects into sections format
      sections = clientProjects.map(project => ({
        subsections: [{
          id: project.id,
          title: project.name,
          intro: project.listData?.intro || project.description,
          role: project.listData?.role,
          type: project.listData?.type,
          tags: project.listData?.tags || project.tags,
          steps: [{ type: 'project', data: project, image: project.image }]
        }]
      }))
    } else if (displayLevel === 3 && displayClient && displayProject) {
      const clientProjects = projectsData[displayClient] || []
      const project = clientProjects.find(p => p.id === displayProject)
      sections = project?.sections || []
    }
    
    const positions = []
    let cumulativePosition = 0
    
    sections.forEach(section => {
      section.subsections?.forEach(subsection => {
        subsection.steps.forEach((step, stepIndex) => {
          const pos = cumulativePosition + (stepIndex * STEP_SPACING)
          positions.push(pos)
        })
        cumulativePosition += (subsection.steps.length - 1) * STEP_SPACING + SECTION_SPACING
      })
    })
    
    return positions
  }, [displayLevel, displayClient, displayProject])

  // Calculate target scene position - snap to index 0 on level change
  useEffect(() => {
    // Don't update positions during level transitions
    if (isLevelTransitioning.current) {
      return
    }
    
    const position = allStepPositions[currentItemIndex] || 0
    
    if (sceneGroupRef.current) {
      targetScenePosition.current.set(-position, 0, 0)
    }
  }, [currentItemIndex, allStepPositions])
  
  // Handle level changes separately
  useEffect(() => {
    const levelChanged = previousLevelRef.current !== currentLevel
    
    if (levelChanged && sceneGroupRef.current) {
      const levelIncreased = currentLevel > previousLevelRef.current
      
      // Set transition state
      isLevelTransitioning.current = true
      
      // PHASE 1: Slide OUT - just move target position offscreen
      const slideOutTarget = levelIncreased ? -15 : 15
      targetScenePosition.current.set(slideOutTarget, 0, 0)
      
      // PHASE 2: After 300ms, switch content and slide IN
      setTimeout(() => {
        // Switch to new level content
        setDisplayLevel(currentLevel)
        setDisplayClient(selectedClient)
        setDisplayProject(selectedProject)
        
        // Wait for content to mount
        requestAnimationFrame(() => {
          // Start new content from opposite side
          const slideInStart = levelIncreased ? 15 : -15
          currentScenePosition.current.set(slideInStart, 0, 0)
          
          // Target is center (0)
          targetScenePosition.current.set(0, 0, 0)
        })
      }, 300)
      
      // End transition after animation completes
      setTimeout(() => {
        isLevelTransitioning.current = false
      }, 650)
      
      previousLevelRef.current = currentLevel
    }
  }, [currentLevel, selectedClient, selectedProject])
  
  // Initialize position on first mount (X-axis for horizontal)
  useEffect(() => {
    if (sceneGroupRef.current && allStepPositions.length > 0 && isInitialMount.current) {
      // Use currentItemIndex to support debug mode starting at different positions
      const position = allStepPositions[currentItemIndex] || 0
      currentScenePosition.current.set(-position, 0, 0)
      sceneGroupRef.current.position.set(-position, 0, 0)
      targetScenePosition.current.set(-position, 0, 0)
      animationProgress.current = 1
      
      // Initialize display state from props (supports debug mode)
      setDisplayLevel(currentLevel)
      setDisplayClient(selectedClient)
      setDisplayProject(selectedProject)
      isInitialMount.current = false
      
      // Log debug mode if active
      if (currentLevel === 3 && selectedClient && selectedProject) {
        console.log('🎯 WorkOverlay initialized with debug mode:', {
          level: currentLevel,
          client: selectedClient,
          project: selectedProject,
          startingIndex: currentItemIndex,
          totalSteps: allStepPositions.length,
          startingPosition: position
        })
      }
    }
  }, [allStepPositions])

  // Ease-in-out function (smooth acceleration and deceleration)
  const easeInOutCubic = (t) => {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2
  }

  // Smooth scene animation with constant lerp
  useFrame((state, delta) => {
    if (sceneGroupRef.current) {
      // Slower, smoother lerp for more visible animation (reduced from 8 to 4)
      const lerpFactor = Math.min(delta * 4, 1)
      currentScenePosition.current.lerp(targetScenePosition.current, lerpFactor)
      
      // Apply position directly - no extra offsets
      sceneGroupRef.current.position.copy(currentScenePosition.current)
    }
  })

  return (
    <>
      {/* Hemisphere light creates gradient background */}
      <hemisphereLight 
        skyColor="#4b6cb7" 
        groundColor="#182848" 
        intensity={2}
      />

      {/* Scene group that moves instead of camera */}
      <group ref={sceneGroupRef}>

        {/* Render content based on DISPLAY level (not current level) for smooth transitions */}
        {displayLevel === 1 && (
          <UnifiedLevelRenderer
            key="level-1"
            sections={experiencesData.sections}
            onCardClick={onSelectClient}
            isMobile={isMobile}
            focusedIndex={focusedIndex}
            setFocusedIndex={setFocusedIndex}
            currentScenePosition={currentScenePosition}
            annotationsEnabled={annotationsEnabled}
          />
        )}

        {displayLevel === 2 && displayClient && (
          <UnifiedLevelRenderer
            key={`level-2-${displayClient}`}
            sections={(projectsData[displayClient] || []).map(project => ({
              subsections: [{
                id: project.id,
                title: project.name,
                hasEnterButton: project.hasEnterButton,
                intro: project.listData?.intro || project.description,
                role: project.listData?.role,
                type: project.listData?.type,
                tags: project.listData?.tags || project.tags,
                steps: [{ type: 'project', data: project, image: project.image }]
              }]
            }))}
            onCardClick={onSelectProject}
            isMobile={isMobile}
            focusedIndex={focusedIndex}
            setFocusedIndex={setFocusedIndex}
            currentScenePosition={currentScenePosition}
            annotationsEnabled={annotationsEnabled}
          />
        )}

        {displayLevel === 3 && displayClient && displayProject && (
          <UnifiedLevelRenderer
            key={`level-3-${displayProject}`}
            sections={(() => {
              const clientProjects = projectsData[displayClient] || []
              const project = clientProjects.find(p => p.id === displayProject)
              return project?.sections || []
            })()}
            currentLevel={displayLevel}
            selectedClient={displayClient}
            selectedProject={displayProject}
            currentItemIndex={currentItemIndex}
            isMobile={isMobile}
            focusedIndex={focusedIndex}
            setFocusedIndex={setFocusedIndex}
            currentScenePosition={currentScenePosition}
            annotationsEnabled={annotationsEnabled}
          />
        )}
      </group>
    </>
  )
}

/**
 * Unified Level Renderer
 * Renders ALL content for a level immediately - no lazy loading, no visibility logic
 */
const UnifiedLevelRenderer = ({ sections, onCardClick, currentLevel, selectedClient, selectedProject, currentItemIndex, isMobile, focusedIndex, setFocusedIndex, currentScenePosition, annotationsEnabled = true }) => {
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)
  
  // Force re-render immediately on mount to make Html components visible
  useEffect(() => {
    forceUpdate()
  }, [])
  
  // Helper function to get screen annotations for a specific subsection
  const getScreenAnnotations = (sectionId, subsectionId) => {
    if (currentLevel !== 3 || !selectedClient || !selectedProject) return []
    
    try {
      const projectAnnotations = screenAnnotationsData[selectedClient]?.[selectedProject]
      if (!projectAnnotations) {
        console.log('❌ No project annotations for:', selectedClient, selectedProject)
        return []
      }
      
      const sectionAnnotations = projectAnnotations[sectionId]
      if (!sectionAnnotations) {
        console.log('❌ No section annotations for:', sectionId)
        return []
      }
      
      const subsectionData = sectionAnnotations[subsectionId]
      console.log('📍 Subsection annotations:', {
        sectionId,
        subsectionId,
        data: subsectionData,
        isArray: Array.isArray(subsectionData)
      })
      
      return subsectionData || []
    } catch (e) {
      console.warn('Failed to get screen annotations:', e)
      return []
    }
  }
  
  let cumulativePosition = 0
  let globalStepIndex = 0
  const allContent = []
  
  sections.forEach((section, sectionIndex) => {
    section.subsections?.forEach((subsection) => {
      // Get all annotations for this subsection
      const subsectionAnnotations = getScreenAnnotations(section.id, subsection.id)
      
      subsection.steps.forEach((step, stepIndex) => {
        const position = cumulativePosition + (stepIndex * STEP_SPACING)
        const currentGlobalIndex = globalStepIndex
        
        // Filter annotations for this specific screen by screenIndex
        const stepScreenAnnotations = Array.isArray(subsectionAnnotations)
          ? subsectionAnnotations.filter(annotation => 
              annotation.screenIndex === undefined || annotation.screenIndex === stepIndex
            )
          : []
        
        if (step.type === 'phone' && stepScreenAnnotations.length > 0) {
          console.log('🎯 Phone step with annotations:', {
            subsectionId: subsection.id,
            stepIndex,
            globalIndex: currentGlobalIndex,
            annotationCount: stepScreenAnnotations.length,
            annotations: stepScreenAnnotations.map(a => ({ id: a.id, screenIndex: a.screenIndex }))
          })
        }
        
        allContent.push(
          <group key={`${section.id}-${subsection.id}-${stepIndex}`} position={[position, 0, 0]}>
            {step.type === 'graphic' && (
              <ProjectImageStep 
                step={step} 
                subsection={subsection}
                onClick={() => {
                  // Only allow focus mode in level 3
                  if (currentLevel === 3) {
                    setFocusedIndex(focusedIndex === currentGlobalIndex ? null : currentGlobalIndex)
                  }
                  onCardClick && onCardClick(subsection.id)
                }}
                isMobile={isMobile}
                isFocused={currentLevel === 3 && focusedIndex === currentGlobalIndex}
              />
            )}
            
            {step.type === 'phone' && (
              <ProjectPhoneStep 
                step={step} 
                isMobile={isMobile}
                screenAnnotations={annotationsEnabled ? stepScreenAnnotations : []}
                showDebugGrid={currentLevel === 3 && currentGlobalIndex === currentItemIndex}
              />
            )}
            
            {step.type === 'project' && step.data && (
              <ProjectImageStep 
                step={{ image: step.data.image }} 
                subsection={{ hasEnterButton: step.data.hasEnterButton }}
                onClick={() => {
                  // Only allow focus mode in level 3
                  if (currentLevel === 3) {
                    setFocusedIndex(focusedIndex === currentGlobalIndex ? null : currentGlobalIndex)
                  }
                  onCardClick && onCardClick(step.data.id)
                }}
                isMobile={isMobile}
                isFocused={currentLevel === 3 && focusedIndex === currentGlobalIndex}
              />
            )}
          </group>
        )
        
        globalStepIndex++
      })
      
      cumulativePosition += (subsection.steps.length - 1) * STEP_SPACING + SECTION_SPACING
    })
  })
  
  return <>{allContent}</>
}

/**
 * Image Step - clickable image using custom rounded 3D mesh model
 */
const ProjectImageStep = ({ step, subsection, onClick, isMobile, isFocused }) => {
  const texture = useTexture(step.image)
  const { viewport, size } = useThree()
  const [isHovered, setIsHovered] = React.useState(false)
  const groupRef = useRef()
  const targetScale = useRef(1)
  const currentScale = useRef(1)
  
  // Load the rounded image plane model
  const { nodes } = useGLTF('/models/model-prop-imagePlane-1.0.glb')
  
  // Check if this subsection has an enter button
  const hasEnterButton = subsection?.hasEnterButton || false
  
  // Animate scale on hover
  useFrame(() => {
    if (!groupRef.current) return
    
    targetScale.current = isHovered && hasEnterButton ? 1.02 : 1
    currentScale.current += (targetScale.current - currentScale.current) * 0.1
    
    groupRef.current.scale.set(currentScale.current, currentScale.current, 1)
  })
  
  // Dynamic frame dimensions based on actual window size
  const aspectRatio = 3 / 2  // 3:2 landscape aspect ratio
  
  // Use window width in pixels to determine scale
  // Smaller windows = smaller images, larger windows = larger images
  // Map window width range to world unit range
  const windowWidth = size.width
  
  // Scale factor for the mesh model (similar to phone scale)
  let meshScale
  if (windowWidth < 480) {
    // Mobile phones: smallest scale for Pixel, iPhone, etc.
    meshScale = 7
  } else if (windowWidth < 768) {
    // Small tablets: small-medium scale
    meshScale = 9
  } else if (windowWidth < 1440) {
    // Tablet/small desktop: medium scale
    meshScale = 10
  } else {
    // Large desktop: larger scale
    meshScale = 12
  }
  
  // Calculate display dimensions for positioning (these are virtual units for layout)
  let frameWidth
  if (windowWidth < 480) {
    // Mobile phones: smaller frame
    frameWidth = 2.2
  } else if (windowWidth < 768) {
    // Small tablets: small-medium frame
    frameWidth = 2.5
  } else if (windowWidth < 1440) {
    frameWidth = 3
  } else {
    frameWidth = 3.5
  }
  
  const frameHeight = frameWidth / aspectRatio
  const frameAspect = aspectRatio
  
  // Calculate image aspect ratio
  const imageAspect = texture.image ? texture.image.width / texture.image.height : frameAspect
  
  // Calculate texture scale for "cover" behavior - FILL ENTIRE MESH and crop edges
  let scaleX = 1
  let scaleY = 1
  
  if (imageAspect > frameAspect) {
    // Image is wider than frame - scale UP horizontally to fill, crop sides
    scaleX = frameAspect / imageAspect
  } else {
    // Image is taller than frame - scale UP vertically to fill, crop top/bottom
    scaleY = imageAspect / frameAspect
  }
  
  texture.repeat.set(scaleX, scaleY)
  texture.offset.set((1 - scaleX) / 2, (1 - scaleY) / 2)
  
  // Position adjustments based on window size
  let xPosition = 0
  let yPosition = 0
  
  if (windowWidth < 480) {
    // Mobile phone positioning
    xPosition = isFocused ? 0 : 0  // Center when focused
    yPosition = isFocused ? 0 : -0.75 // Lower on phone screens
  } else if (windowWidth < 768) {
    // Small tablet positioning
    xPosition = isFocused ? 0 : 0  // Center when focused
    yPosition = isFocused ? 0 : -0.5  // Lower on tablets
  } else if (windowWidth < 1440) {
    // Tablet/small desktop positioning
    xPosition = isFocused ? 0 : 1.5  // Center when focused
    yPosition = isFocused ? 0 : 0  // Center when focused
  } else {
    // Large desktop positioning
    xPosition = isFocused ? 0 : 1.5  // Center when focused
    yPosition = isFocused ? 0 : 0     // Already centered
  }
  
  // Move closer to camera when focused (zoom effect)
  const focusedZOffset = isFocused ? 12 : 10
  
  // Get the mesh from the loaded model
  const imagePlaneMesh = nodes.imagePlane || nodes.Plane || Object.values(nodes).find(node => node.isMesh)
  
  // Calculate button position: center of mesh + slight offset to right
  const buttonPosition = useMemo(() => {
    if (!imagePlaneMesh) return [xPosition, yPosition, focusedZOffset]
    
    // Compute bounding box to get mesh dimensions in local space
    imagePlaneMesh.geometry.computeBoundingBox()
    const bbox = imagePlaneMesh.geometry.boundingBox
    const meshWidth = bbox.max.x - bbox.min.x
    
    // Calculate offset as percentage of mesh width (0.35 = 35% to the right)
    // This stays in mesh-local space, then we apply the mesh scale
    const offsetX = (0.35 * meshWidth) * meshScale
    
    // Button position = mesh position + offset (Y always at mesh center = yPosition)
    return [
      xPosition + offsetX,  // X: mesh center + offset to right
      yPosition,            // Y: mesh center (no offset)
      focusedZOffset + 0.01 // Z: slightly in front
    ]
  }, [imagePlaneMesh, meshScale, xPosition, yPosition, focusedZOffset])

  // flip rotation for blender inmport glb models
  
  // Debug: log what we found
  React.useEffect(() => {
    console.log('Image Plane Model Nodes:', Object.keys(nodes))
    console.log('Selected mesh:', imagePlaneMesh?.name)
    if (imagePlaneMesh) {
      console.log('Mesh geometry bounds:', imagePlaneMesh.geometry.boundingBox)
    }
  }, [nodes, imagePlaneMesh])
  
  // Configure texture settings
  useMemo(() => {
    if (texture) {
      texture.flipY = false // GLB models typically don't need flipY
      texture.colorSpace = THREE.SRGBColorSpace
      texture.minFilter = THREE.LinearFilter
      texture.magFilter = THREE.LinearFilter
    }
  }, [texture])
  
  if (!imagePlaneMesh) {
    console.error('No mesh found in image plane model!')
    return null
  }
  
  return (
    <group ref={groupRef}>
      <mesh
        geometry={imagePlaneMesh.geometry}
        position={[xPosition, yPosition, focusedZOffset]}
        rotation={[0, 0, 0]}
        scale={meshScale}
        onClick={onClick}
        onPointerOver={() => setIsHovered(true)}
        onPointerOut={() => setIsHovered(false)}
      >
        <meshStandardMaterial 
          map={texture} 
          emissive={new THREE.Color(0xffffff)}
          emissiveIntensity={0.8}
          emissiveMap={texture}
          toneMapped={false}
          transparent 
          side={THREE.DoubleSide} 
        />
      </mesh>
      
      {/* Enter Button - center right of image */}
      {hasEnterButton && !isFocused && (
        <Html
          position={buttonPosition}
          center
          distanceFactor={8}
          style={{ pointerEvents: 'none' }}
        >
          <button 
            className={`btn-xlarge canvas-enter-btn ${isHovered ? 'hovered' : ''}`}
            style={{ pointerEvents: 'none' }}
          >
            Explore
            <span className="material-symbols-rounded">chevron_right</span>
          </button>
        </Html>
      )}
    </group>
  )
}

/**
 * Single Phone Step
 */
const ProjectPhoneStep = ({ step, isMobile, screenAnnotations = [], showDebugGrid = false }) => {
  const { size } = useThree()
  const windowWidth = size.width
  const phoneRef = useRef()
  const [isHovered, setIsHovered] = React.useState(false)
  
  // Adjust scale on mobile
  const scale = isMobile ? 30 : 40  // Slightly smaller on mobile
  
  // Position adjustments based on window size
  let yPosition = 0
  
  if (windowWidth < 768) {
    // Mobile positioning - move down
    yPosition = -1
  } else if (windowWidth < 1440) {
    // Tablet/small desktop positioning
    yPosition = 0
  } else {
    // Large desktop positioning
    yPosition = 0
  }
  
  return (
    <group position={[0, yPosition, 0]}>
      <Phone3DModel
        ref={phoneRef}
        screenTexture={step.image}
        position={[0, 0, 0]}
        rotation={[0, 0, 0]}
        scale={scale}
        onPointerOver={() => {
          console.log('Phone hovered')
          setIsHovered(true)
        }}
        onPointerOut={() => {
          console.log('Phone unhovered')
          setIsHovered(false)
        }}
      >
        {/* Render all screen annotations for this step */}
        {Array.isArray(screenAnnotations) && screenAnnotations.map((annotation, idx) => (
          <AnnotationPoint 
            key={`screen-annotation-${annotation.id || idx}`}
            phoneRef={phoneRef}
            offsetX={annotation.offsetX || 0}
            offsetY={annotation.offsetY || 0}
            annotationType={annotation.type || 'focus'}
            direction={annotation.direction || 'left'}
            title={annotation.title || ''}
            description={annotation.description || ''}
            showAnnotationBox={isHovered}
            showDebugGrid={showDebugGrid && idx === 0} // Only show grid for first annotation
          />
        ))}
      </Phone3DModel>
    </group>
  )
}

export default workOverlay

// Preload the image plane model
useGLTF.preload('/models/model-prop-imagePlane-1.0.glb')
